import React, { useMemo } from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Link,
  LinkButton,
} from '@/shared/ui';
import { ExternalLink, Calendar, MapPin, GraduationCap } from 'lucide-react';
import { Education, formatDateRange } from '@personal-portfolio/contract';
import { useSiteContent } from '@personal-portfolio/queries';

interface EducationLink {
  id: number;
  label: string;
  url: string;
  type?: string;
  description?: string;
}

export type EducationCardOnLinkClicked = (
  education: Pick<Education, 'id' | 'institution' | 'degree'>,
  link: EducationLink,
) => void;

export interface EducationCardProps {
  id: number;
  slug: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  level:
    | 'high-school'
    | 'diploma'
    | 'associate'
    | 'bachelor'
    | 'master'
    | 'doctorate'
    | 'certification'
    | 'course'
    | 'bootcamp'
    | 'workshop'
    | 'other';
  startDate: string;
  endDate?: string;
  location?: string;
  gpa?: number;
  description?: string;
  summary?: string;
  achievements?: unknown;
  logo?: {
    id: number;
    url: string;
    alternativeText?: string;
  };
  diploma?: {
    id: number;
    url: string;
    name: string;
  };
  transcript?: {
    id: number;
    url: string;
    name: string;
  };
  links?: EducationLink[];
  isFeatured: boolean;
  onLinkClick?: EducationCardOnLinkClicked;
}

export const EducationCard: React.FC<EducationCardProps> = ({
  id,
  slug,
  institution,
  degree,
  fieldOfStudy,
  level,
  startDate,
  endDate,
  location,
  gpa,
  description,
  summary,
  achievements,
  logo,
  diploma,
  transcript,
  links,
  isFeatured,
  onLinkClick,
}) => {
  const { cards } = useSiteContent();
  const institutionLink = useMemo(() => {
    const companyProfileLink = links?.find(
      (link) => link.type === 'company-profile',
    );
    return companyProfileLink || links?.[0];
  }, [links]);

  return (
    <Card className="group scroll-mt-20 hover:shadow-md" id={slug}>
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-muted text-foreground">
              {logo ? (
                <img
                  src={logo.url}
                  alt={logo.alternativeText || institution}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full rounded-xl object-cover"
                />
              ) : (
                <GraduationCap size={24} />
              )}
            </div>
            <div className="space-y-1">
              <CardTitle className="text-xl font-semibold tracking-tight">
                {institutionLink?.url ? (
                  <Link
                    href={institutionLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="decoration-muted-foreground/40 underline-offset-4 hover:underline"
                  >
                    {institution}
                  </Link>
                ) : (
                  institution
                )}
              </CardTitle>
              <h3 className="mb-1 text-base leading-snug font-semibold text-muted-foreground">
                {fieldOfStudy && fieldOfStudy !== degree
                  ? `${degree}, ${fieldOfStudy}`
                  : degree}
              </h3>
            </div>
          </div>
          {isFeatured && (
            <Badge variant="secondary" className="shrink-0">
              {cards.featured}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 font-mono text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDateRange(startDate, endDate)}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{location}</span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {summary && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.summary}
            </h4>
            <p className="text-sm leading-relaxed break-words text-muted-foreground">
              {summary}
            </p>
          </div>
        )}

        {description && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.description}
            </h4>
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed break-words text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        )}

        {(diploma || transcript) && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.documents}
            </h4>
            <div className="flex flex-wrap gap-2">
              {diploma && (
                <LinkButton
                  variant="outline"
                  size="sm"
                  href={diploma.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cards.diploma}
                  <ExternalLink className="ml-1" />
                </LinkButton>
              )}
              {transcript && (
                <LinkButton
                  variant="outline"
                  size="sm"
                  href={transcript.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cards.transcript}
                  <ExternalLink className="ml-1" />
                </LinkButton>
              )}
            </div>
          </div>
        )}

        {links && links.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.relatedLinks}
            </h4>
            <div className="flex flex-wrap gap-2">
              {links.map((link) => (
                <LinkButton
                  key={link.id}
                  variant="outline"
                  size="sm"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onPress={() =>
                    onLinkClick &&
                    onLinkClick({ id, institution, degree }, link)
                  }
                >
                  {link.label}
                  <ExternalLink className="ml-1" />
                </LinkButton>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
